import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    History as HistoryIcon,
    AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import MarkdownEditor from '../components/MarkdownEditor';
import MarkdownDisplay from '../components/MarkdownDisplay';
import ConfirmDialog from '../components/admin/ConfirmDialog';
import AttachmentBox from '../components/AttachmentBox';
import FilePondUploader from '../components/FilePondUploader';
import { getKBTags, getKBArticles, getKBArticle, createKBArticle, updateKBArticle, deleteKBArticle, getKBArticleVersions } from '../redux/ducks/kb';
import { getClients } from '../redux/ducks/client';
import { showAlert } from '../redux/slices/alerts';
import { formatDateTime, formatTimeAgo } from '../utils/dateUtils';
import { restoreAttachments, transformAttachments } from '../utils/uploadUtils';

const PAGE_SIZE = 25;
const SEARCH_DELAY_MS = 300;
const ALL_CLIENTS = { _id: '', name: 'All clients' };

const clientOptionLabel = (option) => (option.active === false ? `${option.name} (inactive)` : option.name || '');

// ─── Styled Components ───

const FilterRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
}));

const SearchField = styled(TextField)({
    flex: '1 1 280px',
});

const ClientFilter = styled(Autocomplete)(({ theme }) => ({
    flex: '0 1 280px',
    minWidth: 220,
    [theme.breakpoints.down('sm')]: {
        minWidth: 0,
        flex: '1 1 100%',
    },
}));

const TagFilter = styled(Autocomplete)(({ theme }) => ({
    flex: '0 1 280px',
    minWidth: 200,
    [theme.breakpoints.down('sm')]: {
        minWidth: 0,
        flex: '1 1 100%',
    },
}));

const TAG_BAR_LIMIT = 12;

const TagBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
}));

const ArticleCard = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 2.5),
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: theme.shadows[2],
    },
}));

const CardTopRow = styled(Box)({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
});

const CardMeta = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));

const DetailContainer = styled(Box)({
    width: '100%',
    maxWidth: 900,
});

const DetailHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const DetailActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
}));

const MetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const EditorWrapper = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const AttachmentSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

// ─── List View ───

const KBList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(ALL_CLIENTS);
    const [activeTags, setActiveTags] = useState([]);
    const [tagData, setTagData] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const searchTimer = useRef(null);
    const searchInputRef = useRef(null);

    const topTags = useMemo(() => tagData.slice(0, TAG_BAR_LIMIT), [tagData]);
    const allTagNames = useMemo(() => tagData.map(t => t.tag), [tagData]);

    const fetchArticles = useCallback((q, client, tags, from) => {
        setLoading(true);
        const params = { limit: PAGE_SIZE, from };
        if (q && q.trim().length >= 2) params.q = q.trim();
        if (client) params.client = client;
        if (tags.length > 0) params.tags = tags.join(',');
        dispatch(getKBArticles(params, (data) => {
            if (data) {
                setArticles(data.articles || []);
                setTotal(data.total || 0);
            }
            setLoading(false);
        }));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getClients(0, 500, (data) => {
            if (data) setClients(data.clients || data || []);
        }));
        dispatch(getKBTags((data) => {
            setTagData(Array.isArray(data) ? data : []);
        }));
    }, [dispatch]);

    useEffect(() => {
        fetchArticles(search, selectedClient._id, activeTags, page * PAGE_SIZE);
    }, [fetchArticles, search, selectedClient, activeTags, page]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setSearch(value);
            setPage(0);
        }, SEARCH_DELAY_MS);
    };

    const handleClientChange = (_, value) => {
        setSelectedClient(value || ALL_CLIENTS);
        setPage(0);
    };

    const handleTagClick = (tag) => {
        setActiveTags((prev) => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
        setPage(0);
    };

    const handleCreated = (article) => {
        setCreateOpen(false);
        if (article) navigate(`/kb/${article._id}`);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <PageHeader
                title="Knowledge Base"
                subtitle={total > 0 ? `${total} article${total !== 1 ? 's' : ''}` : undefined}
                actions={
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                        New Article
                    </Button>
                }
                filters={
                    <FilterRow>
                        <SearchField
                            size="small"
                            placeholder="Search articles..."
                            inputRef={searchInputRef}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <ClientFilter
                            size="small"
                            options={[ALL_CLIENTS, ...clients]}
                            getOptionLabel={clientOptionLabel}
                            value={selectedClient}
                            onChange={handleClientChange}
                            isOptionEqualToValue={(opt, val) => opt._id === val._id}
                            disableClearable
                            renderInput={(params) => <TextField {...params} label="Client" />}
                        />
                        <TagFilter
                            multiple
                            size="small"
                            options={allTagNames}
                            value={activeTags}
                            onChange={(_, val) => { setActiveTags(val); setPage(0); }}
                            renderInput={(params) => <TextField {...params} label="Tags" />}
                        />
                    </FilterRow>
                }
            />

            {topTags.length > 0 && (
                <TagBar>
                    {topTags.map(({ tag, count }) => (
                        <Chip
                            key={tag}
                            label={`${tag} (${count})`}
                            size="small"
                            variant={activeTags.includes(tag) ? 'filled' : 'outlined'}
                            color={activeTags.includes(tag) ? 'primary' : 'default'}
                            onClick={() => handleTagClick(tag)}
                        />
                    ))}
                </TagBar>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : articles.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {search || selectedClient._id || activeTags.length > 0 ? 'No articles match your filters.' : 'No KB articles yet. Create one to get started.'}
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {articles.map((article) => (
                        <ArticleCard
                            key={article._id}
                            variant="outlined"
                            onClick={() => navigate(`/kb/${article._id}`)}
                        >
                            <CardTopRow>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                                        {article.title}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                                    {formatTimeAgo(article.updatedAt)}
                                </Typography>
                            </CardTopRow>
                            <CardMeta>
                                {article.client?.name && (
                                    <Chip label={article.client.name} size="small" color="default" variant="filled" sx={{ fontWeight: 500 }} />
                                )}
                                {(article.tags || []).map((tag) => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => { e.stopPropagation(); handleTagClick(tag); }}
                                    />
                                ))}
                                {article.updatedBy?.fullName && (
                                    <Typography variant="caption" color="text.secondary">
                                        {article.updatedBy.fullName}
                                    </Typography>
                                )}
                            </CardMeta>
                        </ArticleCard>
                    ))}
                    {total > PAGE_SIZE && (
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={PAGE_SIZE}
                            rowsPerPageOptions={[PAGE_SIZE]}
                        />
                    )}
                </Box>
            )}

            {createOpen && (
                <CreateArticleDialog
                    open={createOpen}
                    clients={clients}
                    onClose={() => setCreateOpen(false)}
                    onCreated={handleCreated}
                />
            )}
        </Box>
    );
};

// ─── Create Dialog ───

const CreateArticleDialog = ({ open, clients, onClose, onCreated }) => {
    const dispatch = useDispatch();
    const [title, setTitle] = useState('');
    const [client, setClient] = useState(null);
    const [tags, setTags] = useState('');
    const [saving, setSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [newAttachments, setNewAttachments] = useState([]);
    const bodyRef = useRef('');

    const handleSave = () => {
        if (!title.trim() || !client) {
            dispatch(showAlert({ severity: 'warning', message: 'Title and client are required' }));
            return;
        }
        if (isUploading) {
            dispatch(showAlert({ severity: 'info', message: 'Please wait for uploads to finish' }));
            return;
        }
        setSaving(true);
        const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);
        const attachments = restoreAttachments(newAttachments);
        dispatch(createKBArticle({
            title: title.trim(),
            body: bodyRef.current,
            client: client._id,
            tags: tagList,
            attachments: attachments.length > 0 ? attachments : undefined,
        }, (article) => {
            setSaving(false);
            if (article) {
                dispatch(showAlert({ severity: 'success', message: 'Article created' }));
                onCreated(article);
            }
        }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>New KB Article</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        autoFocus
                    />
                    <Autocomplete
                        options={clients}
                        getOptionLabel={clientOptionLabel}
                        value={client}
                        onChange={(_, val) => setClient(val)}
                        renderInput={(params) => <TextField {...params} label="Client" required />}
                    />
                    <TextField
                        label="Tags"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        helperText="Comma-separated"
                        fullWidth
                    />
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Body</Typography>
                        <MarkdownEditor
                            autoFocus={false}
                            placeholder="Write your article content..."
                            onChange={(val) => { bodyRef.current = val; }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Attachments</Typography>
                        <FilePondUploader setAttachments={setNewAttachments} onUploadStatusChange={setIsUploading} />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving || isUploading}>
                    {saving ? <CircularProgress size={20} /> : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Detail / Edit View ───

const KBDetail = ({ articleId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [title, setTitle] = useState('');
    const [tags, setTags] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [versionsOpen, setVersionsOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [editAttachments, setEditAttachments] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const bodyRef = useRef('');
    const editorKey = useRef(0);

    useEffect(() => {
        setLoading(true);
        setEditing(false);
        dispatch(getKBArticle(articleId, (data) => {
            if (data) {
                setArticle(data);
                setTitle(data.title || '');
                setTags((data.tags || []).join(', '));
                bodyRef.current = data.body || '';
                editorKey.current += 1;
            }
            setLoading(false);
        }));
    }, [dispatch, articleId]);

    const liveAttachments = useMemo(() => {
        return (article?.attachments || []).filter(a => !a.isDeleted);
    }, [article]);

    const enterEdit = () => {
        setTitle(article.title || '');
        setTags((article.tags || []).join(', '));
        bodyRef.current = article.body || '';
        editorKey.current += 1;
        setExistingAttachments(liveAttachments.map(a => a));
        setEditAttachments([]);
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
    };

    const handleRemoveExisting = (attachment) => {
        setExistingAttachments((prev) => prev.filter(a => a._id !== attachment._id));
    };

    const handleSave = () => {
        if (!title.trim()) {
            dispatch(showAlert({ severity: 'warning', message: 'Title is required' }));
            return;
        }
        if (isUploading) {
            dispatch(showAlert({ severity: 'info', message: 'Please wait for uploads to finish' }));
            return;
        }
        setSaving(true);
        const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);

        const keptIds = existingAttachments.map(a => a._id);
        const newUploads = restoreAttachments(editAttachments);
        const allAttachments = [
            ...keptIds.map(id => ({ id, filename: '', size: 0, mimetype: '', storageKey: '' })),
            ...newUploads,
        ];

        dispatch(updateKBArticle(articleId, {
            title: title.trim(),
            body: bodyRef.current,
            tags: tagList,
            attachments: allAttachments,
        }, (updated) => {
            setSaving(false);
            if (updated) {
                setArticle(updated);
                setEditing(false);
                dispatch(showAlert({ severity: 'success', message: 'Article saved' }));
            }
        }));
    };

    const handleDelete = () => {
        dispatch(deleteKBArticle(articleId, (result) => {
            if (result) {
                dispatch(showAlert({ severity: 'success', message: 'Article deleted' }));
                navigate('/kb');
            }
            setConfirmDelete(false);
        }));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!article) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Article not found.</Typography>
                <Button onClick={() => navigate('/kb')} sx={{ mt: 2 }}>Back to KB</Button>
            </Box>
        );
    }

    return (
        <DetailContainer>
            <DetailHeader>
                <Tooltip title="Back to Knowledge Base">
                    <IconButton onClick={() => navigate('/kb')} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>
                <Typography variant="h5" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {editing ? 'Edit Article' : article.title}
                </Typography>
                <DetailActions>
                    <Tooltip title="Version history">
                        <IconButton onClick={() => setVersionsOpen(true)} size="small">
                            <HistoryIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete article">
                        <IconButton onClick={() => setConfirmDelete(true)} size="small" color="error">
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    {editing ? (
                        <>
                            <Button variant="outlined" onClick={cancelEdit} disabled={saving}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving || isUploading}
                            >
                                Save
                            </Button>
                        </>
                    ) : (
                        <Button variant="contained" startIcon={<EditIcon />} onClick={enterEdit}>
                            Edit
                        </Button>
                    )}
                </DetailActions>
            </DetailHeader>

            <MetaRow>
                <Chip label={article.client?.name || 'No client'} size="small" variant="filled" />
                {article.sourceProject && (
                    <Typography variant="body2" color="text.secondary">
                        Source: {article.sourceProject.name || 'Project'}
                        {article.sourceProject.status ? ` (${article.sourceProject.status})` : ''}
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                    v{article.version} &middot; {formatDateTime(article.updatedAt)}
                </Typography>
                {article.updatedBy?.fullName && (
                    <Typography variant="body2" color="text.secondary">
                        by {article.updatedBy.fullName}
                    </Typography>
                )}
            </MetaRow>

            {editing ? (
                <>
                    <TextField
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Tags"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        helperText="Comma-separated"
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <EditorWrapper>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Body</Typography>
                        <MarkdownEditor
                            key={editorKey.current}
                            initialContent={article.body || ''}
                            autoFocus={false}
                            placeholder="Article content..."
                            onChange={(val) => { bodyRef.current = val; }}
                        />
                    </EditorWrapper>

                    <AttachmentSection>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AttachFileIcon fontSize="small" /> Attachments
                        </Typography>
                        {existingAttachments.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                                {existingAttachments.map((att) => (
                                    <AttachmentBox key={att._id} attachment={att} onRemove={handleRemoveExisting} />
                                ))}
                            </Box>
                        )}
                        <FilePondUploader setAttachments={setEditAttachments} onUploadStatusChange={setIsUploading} />
                    </AttachmentSection>
                </>
            ) : (
                <>
                    {(article.tags || []).length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {article.tags.map((tag) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                        </Box>
                    )}
                    {article.body ? (
                        <MarkdownDisplay content={article.body} />
                    ) : (
                        <Typography color="text.secondary" sx={{ py: 2 }}>No content yet.</Typography>
                    )}

                    {liveAttachments.length > 0 && (
                        <AttachmentSection>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AttachFileIcon fontSize="small" /> Attachments ({liveAttachments.length})
                            </Typography>
                            {liveAttachments.map((att) => (
                                <AttachmentBox key={att._id} attachment={att} />
                            ))}
                        </AttachmentSection>
                    )}
                </>
            )}

            <ConfirmDialog
                open={confirmDelete}
                title="Delete Article"
                message={`Delete "${article.title}"? This cannot be undone from the UI.`}
                onConfirm={handleDelete}
                onClose={() => setConfirmDelete(false)}
            />

            {versionsOpen && (
                <VersionHistoryDialog
                    articleId={articleId}
                    open={versionsOpen}
                    onClose={() => setVersionsOpen(false)}
                />
            )}
        </DetailContainer>
    );
};

// ─── Version History Dialog ───

const VersionHistoryDialog = ({ articleId, open, onClose }) => {
    const dispatch = useDispatch();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        dispatch(getKBArticleVersions(articleId, (result) => {
            if (result) setData(result);
            setLoading(false);
        }));
    }, [dispatch, articleId, open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Version History</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : !data || (data.versions || []).length === 0 ? (
                    <Typography color="text.secondary">No previous versions.</Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Version</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Updated By</TableCell>
                                <TableCell>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...data.versions].reverse().map((v, i) => (
                                <TableRow key={i}>
                                    <TableCell>v{v.version}</TableCell>
                                    <TableCell>{v.title || '—'}</TableCell>
                                    <TableCell>{v.updatedBy?.fullName || '—'}</TableCell>
                                    <TableCell>{formatDateTime(v.updatedAt)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Page Router ───

const KnowledgeBase = () => {
    const { articleId } = useParams();
    return articleId ? <KBDetail articleId={articleId} /> : <KBList />;
};

export default KnowledgeBase;
